import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, CornerDownLeft } from 'lucide-react';
import { TEAMS, type Team } from '@/lib/teams';
import type { Ticket } from '@/lib/mock';
import { cn } from '@/lib/utils';

type Item =
  | { kind: 'team'; team: Team }
  | { kind: 'ticket'; ticket: Ticket };

export function CommandPalette({
  open,
  tickets,
  onClose,
  onOpenTeam,
  onOpenTicket,
}: {
  open: boolean;
  tickets: Ticket[];
  onClose: () => void;
  onOpenTeam: (id: string) => void;
  onOpenTicket: (t: Ticket) => void;
}) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const s = q.toLowerCase().trim();
    const teams = TEAMS.filter((t) => !s || t.name.toLowerCase().includes(s)).slice(0, 6);
    const tk = tickets
      .filter((t) =>
        !s
          ? true
          : `${t.ref} ${t.subject} ${t.team} ${t.assignee ?? ''}`.toLowerCase().includes(s),
      )
      .slice(0, 7);
    return [
      ...teams.map((team) => ({ kind: 'team' as const, team })),
      ...tk.map((ticket) => ({ kind: 'ticket' as const, ticket })),
    ];
  }, [q, tickets]);

  useEffect(() => {
    if (active >= items.length) setActive(Math.max(0, items.length - 1));
  }, [items, active]);

  if (!open) return null;

  const activate = (it: Item) => {
    if (it.kind === 'team') onOpenTeam(it.team.slug);
    else onOpenTicket(it.ticket);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (items[active]) activate(items[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const firstTicketIdx = items.findIndex((i) => i.kind === 'ticket');

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKey}
            placeholder="Jump to a team or ticket…"
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">esc</kbd>
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-1.5">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No matches.</div>
          )}
          {items.map((it, i) => {
            const isActive = i === active;
            const showTeamsHdr = i === 0 && it.kind === 'team';
            const showTicketsHdr = i === firstTicketIdx && it.kind === 'ticket';
            return (
              <div key={it.kind === 'team' ? `t-${it.team.id}` : `k-${it.ticket.id}`}>
                {showTeamsHdr && <Hdr>Teams</Hdr>}
                {showTicketsHdr && <Hdr>Tickets</Hdr>}
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => activate(it)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition',
                    isActive ? 'bg-primary/10' : 'hover:bg-muted/60',
                  )}
                >
                  {it.kind === 'team' ? (
                    <>
                      <span
                        className="flex h-6 w-6 items-center justify-center rounded-md"
                        style={{ background: `hsl(${it.team.hue} 65% 52% / 0.15)`, color: `hsl(${it.team.hue} 65% 52%)` }}
                      >
                        <it.team.icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="flex-1 font-medium">{it.team.name}</span>
                      <span className="text-[11px] text-muted-foreground">Team</span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-[11px] text-muted-foreground">{it.ticket.ref}</span>
                      <span className="flex-1 truncate">{it.ticket.subject}</span>
                      <span className="truncate text-[11px] text-muted-foreground">{it.ticket.team}</span>
                    </>
                  )}
                  {isActive && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Hdr({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </div>
  );
}
